import { useMemo } from 'react';

import { BarChartTooltipOverlay } from '../BarChartTooltipOverlay/BarChartTooltipOverlay';
import {
    DEFAULT_Y_AXIS_VALUES,
    MAX_PERCENTAGE,
    MIN_PERCENTAGE,
    calculateMaxAxisTick,
    calculateMinAxisTick,
    formatTooltipText,
    generateYAxisValues,
    getBadgeColor,
    getBadgeTextColor,
    getResponseLabel,
} from '../RankingQuestionResult/utils/chartCalculations';

import type { BarChartValue } from '../BarChart/BarChart';

import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';

export interface EmojiChoiceAnswer {
    /** Emoji character or icon identifier */
    src?: string;
    /** Emoji label */
    label: string;
    /** Number of responses */
    count: number;
}

export interface EmojiChoiceResultProps {
    /** Emoji options ordered from best (index 0) to worst */
    options: EmojiChoiceAnswer[];
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

/**
 * EmojiChoiceResult
 *
 * Displays emoji choice question results using a bar chart with emoji icons.
 * Uses the shared BarChartTooltipOverlay for consistent styling with other chart types.
 * Options are displayed in natural order: worst (left) to best (right).
 */
const EmojiChoiceResult = ({
    options,
    className = '',
    dataTestId = 'emoji-choice-result',
}: EmojiChoiceResultProps) => {
    const { t } = useAmelioTranslations();
    const totalResponses = useMemo(() => options.reduce((sum, o) => sum + o.count, 0), [options]);

    const percentages = useMemo(() => {
        return options.map((option) =>
            totalResponses > 0 ? Math.round((option.count / totalResponses) * 100) : 0,
        );
    }, [options, totalResponses]);

    const yAxisConfig = useMemo(() => {
        if (percentages.length === 0) {
            return {
                yValues: DEFAULT_Y_AXIS_VALUES,
                maxYValue: MAX_PERCENTAGE,
                minYValue: MIN_PERCENTAGE,
            };
        }

        const validPercentages = percentages.filter((p) => p >= 0);
        const minPercentage = Math.min(...validPercentages);
        const maxPercentage = Math.max(...validPercentages);

        const minYValue = calculateMinAxisTick(minPercentage, MIN_PERCENTAGE);
        const maxYValue = calculateMaxAxisTick(maxPercentage, MAX_PERCENTAGE);
        const yValues = generateYAxisValues(minYValue, maxYValue);

        return { yValues, maxYValue, minYValue };
    }, [percentages]);

    const barValues: BarChartValue[] = useMemo(() => {
        return options.map((option, displayIndex) => ({
            label: option.label,
            value: percentages[displayIndex],
            icon: option.src,
            badgeColor: getBadgeColor(displayIndex),
            badgeTextColor: getBadgeTextColor(displayIndex),
        }));
    }, [options, percentages]);

    const renderTooltipContent = (barValue: BarChartValue, index: number) => {
        const count = options[index]?.count ?? 0;
        const percentage = barValue.value;
        const label = getResponseLabel({
            count,
            singularLabel: t('common.response'),
            pluralLabel: t('common.responses'),
        });

        const tooltipText = formatTooltipText({
            percentage,
            count,
            responseLabel: label,
        });

        return tooltipText;
    };

    if (!options.length) {
        return null;
    }

    return (
        <div
            className={className}
            data-testid={dataTestId}
        >
            <BarChartTooltipOverlay
                values={barValues}
                yValues={yAxisConfig.yValues}
                maxYValue={yAxisConfig.maxYValue}
                minYValue={yAxisConfig.minYValue}
                dataTestId={`${dataTestId}-chart`}
                renderTooltipContent={renderTooltipContent}
            />
        </div>
    );
};

export { EmojiChoiceResult };
export default EmojiChoiceResult;
